import { useState } from 'react';
import { Loader2, MessageSquareShare } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useResendNotification } from '@/lib/notifications/use-notifications';
import { useUpdateSubCaseContact } from '@/lib/cases/contact';
import type { DisruptionPnrGroupRow } from '@/lib/disruptions/types';
import { queueResend, type ContactPreferredChannel, type ResendChannel } from './use-resend-offer';
import { RevealableContact } from './revealable-contact';

// Correcting a wrong phone / email for one PNR, chained into a resend prompt (the
// corrected value only takes effect on the NEXT send). Opened from the passenger card's
// utilities (`usePnrUtilities`).

/**
 * Channel preselected on the post-correction resend prompt: a corrected phone
 * defaults the resend to SMS, a corrected email to EMAIL; when both were
 * corrected — or neither (a preference-only change) — the default is to keep
 * whatever channel the last notification went out on ('' = no override).
 */
function defaultResendChannel(
  patch: { email?: string; phone?: string },
): '' | ContactPreferredChannel {
  if (patch.phone && !patch.email) return 'SMS';
  if (patch.email && !patch.phone) return 'EMAIL';
  return '';
}

export function UpdateContactDialog({
  group,
  open,
  onClose,
}: {
  group: DisruptionPnrGroupRow;
  open: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation('pnrActions');
  const update = useUpdateSubCaseContact(group.caseUrn, group.disruptionUrn);
  const resend = useResendNotification(group.caseUrn);
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [preferredChannel, setPreferredChannel] = useState<'' | ContactPreferredChannel>('');
  const [error, setError] = useState<string | null>(null);
  // Post-save send prompt ('' channel = current preference).
  const [step, setStep] = useState<'edit' | 'resend'>('edit');
  const [resendChannel, setResendChannel] = useState<'' | ResendChannel>('');

  const dirty = email.trim() !== '' || phone.trim() !== '' || preferredChannel !== '';
  const pax = group.passengers?.[0];
  const persistedContact = pax ? (
    <div className="space-y-2 rounded-md border border-border p-3" data-testid="contact-current">
      <p className="text-sm font-medium">{t('utility.contact.current')}</p>
      <p className="text-xs text-muted-foreground">{t('utility.contact.verify')}</p>
      <RevealableContact caseUrn={group.caseUrn} paxUrn={pax.paxUrn}
        contactOnFile={group.contactOnFile ?? pax.contactOnFile} verifyCurrent />
    </div>
  ) : <p className="text-sm text-muted-foreground">{t('utility.contact.verificationUnavailable')}</p>;

  function close(): void {
    setError(null);
    setStep('edit');
    setResendChannel('');
    onClose();
  }

  async function submit(): Promise<void> {
    setError(null);
    const patch = {
      ...(email.trim() ? { email: email.trim() } : {}),
      ...(phone.trim() ? { phone: phone.trim() } : {}),
      ...(preferredChannel ? { preferredChannel } : {}),
    };
    try {
      await update.mutateAsync({ subCaseUrn: group.subCaseUrn, ...patch });
      // The acknowledged write is explicit; verification still reads from the
      // audited endpoint, never from these submitted form values.
      setResendChannel(defaultResendChannel(patch));
      setEmail('');
      setPhone('');
      setPreferredChannel('');
      setStep('resend');
    } catch (e) {
      const status = (e as { response?: { status?: number } })?.response?.status;
      setError(
        status === 400 ? t('utility.contact.invalid') : t('utility.contact.failed'),
      );
    }
  }

  function confirmResend(): void {
    queueResend(resend, group.subCaseUrn, resendChannel || undefined, t, {
      noChannelNote: t('utility.toast.sameChannelNote'),
      onQueued: close,
    });
  }

  if (step === 'resend') {
    return (
      <Dialog open={open} onOpenChange={(v) => !v && close()}>
        <DialogContent className="z-[60] max-w-md">
          <DialogHeader>
            <DialogTitle>{t('utility.resend.title', { pnr: group.pnrLabel })}</DialogTitle>
            <DialogDescription>{t('utility.resend.description')}</DialogDescription>
          </DialogHeader>

          <p role="status" className="text-sm font-medium text-emerald-700 dark:text-emerald-400" data-testid="contact-saved">
            {t('utility.toast.contactUpdated')}
          </p>
          {persistedContact}

          <label className="block space-y-1 text-sm">
            <span className="text-xs uppercase text-muted-foreground">
              {t('utility.resend.channelLabel')}
            </span>
            <select
              className="w-full rounded-md border border-input bg-background px-2 py-1.5"
              value={resendChannel}
              onChange={(e) => setResendChannel(e.target.value as typeof resendChannel)}
              data-testid="contact-resend-channel"
            >
              <option value="">{t('utility.resend.sameAsLast')}</option>
              <option value="SMS">{t('utility.send.sms')}</option>
              <option value="WHATSAPP">{t('utility.send.whatsapp')}</option>
              <option value="EMAIL">{t('utility.send.email')}</option>
            </select>
          </label>

          <DialogFooter>
            <Button variant="ghost" onClick={close} data-testid="contact-resend-skip">
              {t('utility.resend.skip')}
            </Button>
            <Button
              onClick={confirmResend}
              disabled={resend.isPending}
              data-testid="contact-resend-confirm"
            >
              {resend.isPending ? (
                <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" aria-hidden />
              ) : (
                <MessageSquareShare className="mr-1 h-3.5 w-3.5" aria-hidden />
              )}
              {t('utility.resend.confirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={(v) => !v && close()}>
      <DialogContent className="z-[60] max-w-md">
        <DialogHeader>
          <DialogTitle>{t('utility.contact.title', { pnr: group.pnrLabel })}</DialogTitle>
          <DialogDescription>{t('utility.contact.description')}</DialogDescription>
        </DialogHeader>

        {persistedContact}

        <div className="space-y-3 text-sm">
          <label className="block space-y-1">
            <span className="text-xs uppercase text-muted-foreground">
              {t('utility.contact.phoneLabel')}
            </span>
            <input
              className="w-full rounded-md border border-input bg-background px-2 py-1.5"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder={t('utility.contact.phonePlaceholder')}
              data-testid="contact-phone-input"
            />
          </label>
          <label className="block space-y-1">
            <span className="text-xs uppercase text-muted-foreground">
              {t('utility.contact.emailLabel')}
            </span>
            <input
              type="email"
              className="w-full rounded-md border border-input bg-background px-2 py-1.5"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t('utility.contact.emailPlaceholder')}
              data-testid="contact-email-input"
            />
          </label>
          <label className="block space-y-1">
            <span className="text-xs uppercase text-muted-foreground">
              {t('utility.contact.preferredChannelLabel')}
            </span>
            <select
              className="w-full rounded-md border border-input bg-background px-2 py-1.5"
              value={preferredChannel}
              onChange={(e) => setPreferredChannel(e.target.value as typeof preferredChannel)}
              data-testid="contact-channel-select"
            >
              <option value="">{t('utility.contact.keepCurrent')}</option>
              <option value="SMS">{t('utility.send.sms')}</option>
              <option value="WHATSAPP">{t('utility.send.whatsapp')}</option>
              <option value="EMAIL">{t('utility.send.email')}</option>
            </select>
          </label>

          {error ? (
            <p className="rounded-md border border-destructive/30 bg-destructive/5 p-2 text-destructive" role="alert">
              {error}
            </p>
          ) : null}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={close}>
            {t('common:common.cancel')}
          </Button>
          <Button
            onClick={() => void submit()}
            disabled={!dirty || update.isPending}
            data-testid="contact-save"
          >
            {update.isPending ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" aria-hidden /> : null}
            {t('utility.contact.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
